import {
	ActivityIdRouteSync,
	AppUpdateBanner,
	DocumentLanguageSync,
	LiveUpdateBanner,
	NativeUpdateGate,
	OverlayProvider,
	TenantBrandingProvider,
	ThemeProvider,
	ToastContainer,
	TranslationProvider,
	useTranslation,
	type TenantBrandingValue,
} from '@supply-mover-2-0/web-ui';
import { TEXT_MESSAGES_PATH } from '@supply-mover-2-0/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BrandThemeApplier } from './components/BrandThemeApplier';
import { HelpChatWidget } from './components/HelpChatWidget';
import { LoginModal } from './components/LoginModal';
import { MeetingAddressRedirect } from './components/MeetingAddressRedirect';
import { NativeSplashController } from './components/NativeSplashController';
import { PortalAttributionHydrator } from './components/PortalAttributionHydrator';
import { PortalIdleTimeout } from './components/PortalIdleTimeout';
import { StorefrontHead } from './components/StorefrontHead';
import { StorefrontHeartbeat } from './components/StorefrontHeartbeat';
import { NavigateRememberingReturn } from './components/ReturnAfterSignIn';
import { ScrollToTop } from './components/ScrollToTop';
import { TenantGate } from './components/TenantGate';
import { PortalLayout } from './components/layout/PortalLayout';
import { useDisplaySettings } from './hooks/useDisplaySettings';
import { usePortalAuth } from './hooks/usePortalAuth';
import { AccountDeletedPage } from './pages/AccountDeletedPage';
import { TextMessagesPage } from './pages/TextMessagesPage';
import { AccountPage } from './pages/AccountPage';
import { AppPromotionPage } from './pages/AppPromotionPage';
import { BookingManagePage } from './pages/BookingManagePage';
import { BookingPage } from './pages/BookingPage';
import { BookingPersonPage } from './pages/BookingPersonPage';
import { BoothLinkPage } from './pages/BoothLinkPage';
import { CartPage } from './pages/CartPage';
import { CatalogPage } from './pages/CatalogPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ContactDetailsPage } from './pages/ContactDetailsPage';
import { ClientSelectorPage } from './pages/ClientSelectorPage';
import { DashboardPage } from './pages/DashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { LineReceiptPage } from './pages/LineReceiptPage';
import { MakePaymentPage } from './pages/MakePaymentPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { OrdersPage } from './pages/OrdersPage';
import { PaymentLinkPage } from './pages/PaymentLinkPage';
import { PaymentMethodLinkPage } from './pages/PaymentMethodLinkPage';
import { SentFormPage } from './pages/SentFormPage';
import { PaymentMethodsPage } from './pages/PaymentMethodsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { PromoDetailPage } from './pages/PromoDetailPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ShortLinkPage } from './pages/ShortLinkPage';
import { SignInLinkPage } from './pages/SignInLinkPage';
import { StatementDetailPage } from './pages/StatementDetailPage';
import { StatementsPage } from './pages/StatementsPage';
import { SupportIssueDetailPage } from './pages/SupportIssueDetailPage';
import { SupportPage } from './pages/SupportPage';
import { TransactionDetailPage } from './pages/TransactionDetailPage';
import { TransactionsPage } from './pages/TransactionsPage';
import {
	getPreferredLanguage,
	getTranslations,
} from './services/portalLanguageService';
import { useLoginModalStore } from './stores/loginModalStore';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30000,
			refetchOnWindowFocus: false,
		},
	},
});

/**
 * Build-time brand default for the "Powered by Workd" attribution footer
 * (PD-14113). The storefront is multi-tenant (resolved by hostname), so unlike
 * the brand-built CRM there is no per-brand build — the default is always the
 * Workd platform credit. The backend bootstrap may override this per
 * tenant/storefront (applied by <PortalAttributionHydrator />). Logo URLs match
 * the Workd brand JSON (apps/crm-web/brands/workd.json -> defaultAttribution).
 */
const WORKD_BRAND_DEFAULT: TenantBrandingValue = {
	brandAlias: 'workd',
	brandDisplayName: 'Workd',
	attribution: {
		show: true,
		label: 'Workd',
		logoUrl:
			'https://cdn.titanworkflow.com/titanworkflow/assets/img/workd-email-logo.png',
		logoUrlDark:
			'https://cdn.titanworkflow.com/titanworkflow/assets/img/workd_logo_white.png',
		linkUrl: 'https://workd.com',
	},
};

/**
 * Connects the global login modal store to the LoginModal component.
 * Rendered inside TenantGate so it has access to the tenant context.
 */
function GlobalLoginModal(): JSX.Element {
	const { isOpen, message, close } = useLoginModalStore();
	return <LoginModal isOpen={isOpen} onClose={close} message={message} />;
}

/**
 * Route guard for catalog/browse surfaces. Account-portal-only storefronts
 * (allowCatalogBrowsing off) have no catalog, so a direct URL to /catalog or
 * a promo deep-link redirects to the dashboard rather than rendering a page
 * whose API calls now 403. The nav already hides these entry points; this is
 * the backstop for hand-typed URLs and stale bookmarks.
 */
function RequireCatalogBrowsing({
	children,
}: {
	children: JSX.Element;
}): JSX.Element {
	const display = useDisplaySettings();
	return display.canBrowseCatalog ? (
		children
	) : (
		<Navigate to="/dashboard" replace />
	);
}

/**
 * Route guard for the cart. Browse-only and account-portal-only storefronts
 * have purchasing disabled, so /cart redirects to the dashboard.
 */
function RequirePurchasing({
	children,
}: {
	children: JSX.Element;
}): JSX.Element {
	const display = useDisplaySettings();
	return display.canPurchase ? children : <Navigate to="/dashboard" replace />;
}

/**
 * Route guard for the invoices / statements / transactions surfaces. These
 * sections are hidden entirely for hide-pricing portal users (getMe forces
 * canViewInvoices=false) as well as for storefronts whose invoices section
 * is disabled. The nav already hides these entry points; this is the backstop
 * for hand-typed URLs and stale bookmarks.
 */
function RequireInvoices({ children }: { children: JSX.Element }): JSX.Element {
	const display = useDisplaySettings();
	const { isAuthenticated } = usePortalAuth();
	if (display.canViewInvoices) {
		return children;
	}
	// PD-16863 — a guest on a guest-browsing storefront who followed a link
	// to one of these pages (a statement text) is asked to sign in and comes
	// back here afterwards, instead of being left on the dashboard.
	if (!isAuthenticated) {
		return <NavigateRememberingReturn to="/dashboard" openSignIn />;
	}
	// PD-14239: block invoices/statements/transactions for hide-pricing users.
	return <Navigate to="/dashboard" replace />;
}

/**
 * Route guard for the payment surfaces (make a payment, pay accounts, manage
 * saved payment methods). Disabled for hide-pricing portal users (getMe forces
 * canPayInvoices=false) and for storefronts without payments enabled. The nav
 * and pay actions already hide these entry points; this is the backstop for
 * hand-typed URLs and stale bookmarks.
 */
function RequirePayments({ children }: { children: JSX.Element }): JSX.Element {
	const display = useDisplaySettings();
	// PD-14239: block payment surfaces for hide-pricing users.
	return display.canPayInvoices ? (
		children
	) : (
		<Navigate to="/dashboard" replace />
	);
}

/**
 * PD-15030 — fire the initial translation load.
 *
 * TranslationProvider deliberately does not load on mount (the CRM defers to
 * its login flow), so every trigger in this app lived on the auth lifecycle:
 * sign-in, session restore, or the language switcher. A signed-out shopper
 * landing fresh hits none of them, so their persisted language choice was
 * never even fetched and the storefront — the sign-in page included —
 * rendered in English. One forced load at mount; the auth lifecycle's own
 * refreshes still run afterwards and win with the signed-in user's server
 * preference when it differs.
 */
function TranslationBootstrap(): null {
	const { refreshTranslations } = useTranslation();
	const fired = useRef(false);

	useEffect(() => {
		if (fired.current) {
			return;
		}
		fired.current = true;
		void refreshTranslations();
	}, [refreshTranslations]);

	return null;
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<ActivityIdRouteSync />
				{/* Reset window scroll on push/replace navigations (PD-14672) —
				    the storefront scrolls the window, so without this a detail
				    page opened from a scrolled list starts mid-scroll. */}
				<ScrollToTop />
				<ThemeProvider initialTheme="light">
					{/* PD-15550 — the storefront had no transient notification
					    surface, so an action that finishes by closing its own
					    panel (paying an invoice) left nothing on screen saying
					    it worked. Mounted here rather than inside TenantGate so
					    the gate and 404 paths can raise one too; the container
					    portals to <body> at z-index 10000, which puts a toast
					    above the Sidebar panels it is usually reporting on. */}
					<ToastContainer />
					<TranslationProvider
						fetchTranslations={getTranslations}
						fetchPreferredLanguage={getPreferredLanguage}
					>
						<TranslationBootstrap />
						{/* Keeps <html lang> on the chosen language (PD-16672). */}
						<DocumentLanguageSync />
						{/* Web update notice (PD-15236) — polls version.json and
						    offers / quietly applies new builds. No product name:
						    the storefront is white-label, so the copy stays
						    brand-neutral. No-op on the native storefront apps
						    (their code ships via the stores) and in dev. */}
						<AppUpdateBanner
							buildStamp={
								typeof __WORKD_BUILD__ !== 'undefined' ? __WORKD_BUILD__ : null
							}
						/>
						{/* Native-shell counterpart (PD-15630): update prompt /
						    blocking gate when a newer STORE build is actually
						    downloadable. Inert in plain browsers. */}
						<NativeUpdateGate apiBaseUrl={import.meta.env.VITE_API_URL ?? ''} />
						{/* Live web-bundle updates for the native storefront apps
						    (PD-13933). Reports the running bundle healthy to the
						    on-device updater as soon as it mounts, so it stays
						    this high in the tree. Inert in browsers. */}
						<LiveUpdateBanner apiBaseUrl={import.meta.env.VITE_API_URL ?? ''} />
						<OverlayProvider>
							<TenantBrandingProvider initialValue={WORKD_BRAND_DEFAULT}>
								{/* Sibling of TenantGate (not a child) so the native
								    splash hides even when the gate resolves to the
								    404 page (PD-14693). */}
								<NativeSplashController />
								{/* PD-17174 — title, robots, canonical, share-card and
								    structured-data tags for the page on screen. A sibling
								    of TenantGate so its not-found page gets noindex too. */}
								<StorefrontHead />
								<TenantGate>
									{/* Side-effect component: applies brand colors,
								    favicon, and meta theme-color from the
								    portal_site bootstrap response. Renders nothing. */}
									<BrandThemeApplier />
									{/* Side-effect: applies the resolved "Powered by X"
									    attribution override from the bootstrap onto the
									    TenantBrandingProvider context (PD-14113). */}
									<PortalAttributionHydrator />
									<MeetingAddressRedirect />
									<GlobalLoginModal />
									{/* PD-16414 — idle sign-out. Inert until a shopper
									    is signed in and the tenant sets storefront
									    minutes. */}
									<PortalIdleTimeout />
									{/* PD-17191 — "still here" heartbeat for visit tracking. */}
									<StorefrontHeartbeat />
									{/* Customer-support widget floats across every
								    portal page. Mounts inside TenantGate so it
								    has tenant context, and inside PortalAuth's
								    reach so it can read the user's token. */}
									<HelpChatWidget />
									<Routes>
										{/* PD-17174 — `/index.html` is the root under another
										    name (CloudFront's default root object, and a path a
										    native shell could load). It used to land on `/` by way
										    of the catch-all redirect, which is now a 404 page. */}
										<Route
											path="/index.html"
											element={<Navigate to="/" replace />}
										/>
										{/* Client selector — separate page outside PortalLayout */}
										<Route
											path="/select-client"
											element={<ClientSelectorPage />}
										/>

										{/* Self-serve customer registration — public,
									    chrome-less, sits outside PortalLayout because
									    the visitor is by definition unauthenticated.
									    The page itself gates on portal_site
									    isRegistrationEnabled before showing the form. */}
										<Route path="/register" element={<RegisterPage />} />

										{/* Password recovery / new-user flows. Outside
									    PortalLayout so they render without nav chrome
									    (the user usually arrives via an emailed link
									    while signed out). */}
										<Route
											path="/forgot-password"
											element={<ForgotPasswordPage />}
										/>
										<Route
											path="/reset-password"
											element={<SetPasswordPage mode="reset" />}
										/>
										<Route
											path="/set-password"
											element={<SetPasswordPage mode="set-initial" />}
										/>
										{/* PD-16471 — tap-to-sign-in links from a code
									    message or a welcome text. */}
										<Route path="/sign-in-link" element={<SignInLinkPage />} />
										{/* PD-17144 — short links from texts, hosted on the
									    storefront so customers see its own domain. */}
										<Route path="/s/:code" element={<ShortLinkPage />} />
										{/* PD-16558 — the storefront's app page; welcome
									    links land here when the storefront has an app. */}
										<Route path="/app" element={<AppPromotionPage />} />
										{/* PD-16674 — public payment links. Outside PortalLayout
									    and every signed-in route guard on purpose: the
									    visitor is paying or saving a card WITHOUT signing
									    in. The link (checked by the API on every call) is
									    the only credential. */}
										<Route path="/pay/:token" element={<PaymentLinkPage />} />
										<Route
											path="/payment-method/:token"
											element={<PaymentMethodLinkPage />}
										/>
										{/* PD-13768 — a form sent to complete and sign, without signing in;
									    the link is the only credential, as for payment links. */}
										<Route path="/form/:token" element={<SentFormPage />} />
										{/* PD-17646 — booking pages. Guests are not signed in and
									    never will be; `meeting` is a reserved name, so the
									    fixed path can't collide with a person's. */}
										<Route path="/book/meeting/:token" element={<BookingManagePage />} />
										<Route path="/book/booth" element={<BoothLinkPage />} />
										<Route path="/book/booth/:kit" element={<BoothLinkPage />} />
										<Route path="/book/:person/:type" element={<BookingPage />} />
										<Route path="/book/:person" element={<BookingPersonPage />} />
										{/* PD-16721 — confirmation after a user deletes their
									    own account; they are signed out by then. */}
										<Route
											path="/account-deleted"
											element={<AccountDeletedPage />}
										/>
										{/* PD-17137 — the brand's texting terms and privacy, which
										    the texting disclosure links; public on every storefront. */}
										<Route
											path={TEXT_MESSAGES_PATH}
											element={<TextMessagesPage />}
										/>

										{/* All portal routes wrapped in PortalLayout */}
										<Route path="/" element={<PortalLayout />}>
											<Route index element={<HomePage />} />
											{/* PD-16862 — the brand website's Login button lands here. */}
											<Route path="login" element={<LoginPage />} />
											<Route path="dashboard" element={<DashboardPage />} />
											<Route
												path="catalog"
												element={
													<RequireCatalogBrowsing>
														<CatalogPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="product/:id"
												element={
													<RequireCatalogBrowsing>
														<ProductDetailPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="promos/:id"
												element={
													<RequireCatalogBrowsing>
														<PromoDetailPage />
													</RequireCatalogBrowsing>
												}
											/>
											<Route
												path="cart"
												element={
													<RequirePurchasing>
														<CartPage />
													</RequirePurchasing>
												}
											/>
											<Route path="orders" element={<OrdersPage />} />
											<Route path="orders/:id" element={<OrderDetailPage />} />
											{/* Receipt-style line detail — always works (reads the
											    order/invoice, never the catalog), so no route guard;
											    the catalog gate is handled by where it's linked from. */}
											<Route
												path="orders/:id/line/:lineId"
												element={<LineReceiptPage source="order" />}
											/>
											<Route
												path="invoices"
												element={
													<RequireInvoices>
														<InvoicesPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="invoices/:id"
												element={
													<RequireInvoices>
														<InvoiceDetailPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="invoices/:id/line/:lineId"
												element={
													<RequireInvoices>
														<LineReceiptPage source="invoice" />
													</RequireInvoices>
												}
											/>
											<Route
												path="statements"
												element={
													<RequireInvoices>
														<StatementsPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="transactions"
												element={
													<RequireInvoices>
														<TransactionsPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="transactions/:id"
												element={
													<RequireInvoices>
														<TransactionDetailPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="statements/:id"
												element={
													<RequireInvoices>
														<StatementDetailPage />
													</RequireInvoices>
												}
											/>
											<Route
												path="payments/make"
												element={
													<RequirePayments>
														<MakePaymentPage />
													</RequirePayments>
												}
											/>
											{/* Support requests (PD-13773) — login-gated inside the
											    pages themselves (no portal feature flag; issue
											    submission/visibility is available on all portals). */}
											<Route path="support" element={<SupportPage />} />
											<Route
												path="support/:id"
												element={<SupportIssueDetailPage />}
											/>
											<Route path="account" element={<AccountPage />} />
											<Route
												path="account/payment-methods"
												element={
													<RequirePayments>
														<PaymentMethodsPage />
													</RequirePayments>
												}
											/>
											<Route
												path="account/password"
												element={<ChangePasswordPage />}
											/>
											{/* PD-16726 — change, add or remove the sign-in email and phone. */}
											<Route
												path="account/contact"
												element={<ContactDetailsPage />}
											/>
											{/* PD-16475 — notification center settings. */}
											<Route
												path="account/notifications"
												element={<NotificationSettingsPage />}
											/>

											{/* PD-17174 — anything else is a real not-found page.
											    Redirecting every unknown URL to the home page (as this
											    route used to) is a "soft 404" to search engines: a
											    removed product's URL would keep answering with the home
											    page. The edge function answers crawlers with HTTP 404
											    for the same paths (libs/shared STOREFRONT_ROUTES lists
											    the routes above, and a test keeps the two in step). */}
											<Route
												path="*"
												element={<NotFoundPage variant="inline" />}
											/>
										</Route>
									</Routes>
								</TenantGate>
							</TenantBrandingProvider>
						</OverlayProvider>
					</TranslationProvider>
				</ThemeProvider>
			</BrowserRouter>
		</QueryClientProvider>
	);
}

export default App;
